import { clsx } from "clsx";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type { MouseEvent } from "react";
import { useId, useMemo, useRef, useState } from "react";
import { useTranslations } from "../../i18n";
import { Dropdown, useDropdownContext } from "../Dropdown";
import { Text } from "../Text";
import { useMenuContext } from "./context";
import { MenuContent } from "./MenuContent";
import type { IMenuOption } from "./types";
import { isMenuOption } from "./types";

type MenuOptionItemProps = {
  option: IMenuOption;
  index: number;
};

export const MenuOptionItem = ({ option, index }: MenuOptionItemProps) => {
  const { t } = useTranslations();
  const { onSelect, rtl } = useMenuContext();
  const {
    navigationRef,
    getItemProps,
    activeIndex,
    dismiss,
    contentId: parentContentId,
  } = useDropdownContext();

  const [open, setOpen] = useState<boolean>(false);
  const isActive = activeIndex === index;

  const optionId = `${parentContentId}-option-${index}`;
  const contentId = useId();

  const submenuTypeaheadListRef = useRef<(string | null)[]>([]);
  useMemo(() => {
    if (option.options) {
      submenuTypeaheadListRef.current = option.options
        .filter(isMenuOption)
        .map((opt) => opt.text);
    }
  }, [option.options]);

  const handleSelect = (e: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (option.options) {
      return;
    }

    onSelect(option.id);
    // Closes the whole tree; the root Dropdown returns focus to its trigger.
    dismiss();
  };

  const hasSubmenu = !!option.options && !option.disabled;

  // A submenu trigger gets its item props from its own Dropdown instead.
  const triggerElement = (
    <button
      {...(option.dataAttributes || {})}
      id={optionId}
      role="menuitem"
      aria-disabled={option.disabled}
      aria-haspopup={hasSubmenu ? "menu" : undefined}
      aria-expanded={hasSubmenu ? open : undefined}
      className={clsx(
        "nuco-menu__option",
        option.disabled && "nuco-menu__option--disabled",
        option.className,
      )}
      disabled={option.disabled}
      ref={navigationRef(index)}
      {...(!hasSubmenu ? getItemProps?.() : {})}
      onClick={handleSelect}
      tabIndex={isActive ? 0 : -1}
    >
      {option.icon && (
        <span className="nuco-menu__option-startAccessory">{option.icon}</span>
      )}
      <Text className="flex-1 text-start">{option.text}</Text>
      {option.accessory && option.accessory}
      {hasSubmenu && (
        <span className="nuco-menu__option-endAccessory">
          {rtl ? (
            <ChevronLeftIcon size="0.875rem" />
          ) : (
            <ChevronRightIcon size="0.875rem" />
          )}
        </span>
      )}
    </button>
  );

  if (option.options && hasSubmenu) {
    return (
      <Dropdown
        trigger={triggerElement}
        content={<MenuContent options={option.options} />}
        triggerId={optionId}
        contentId={contentId}
        open={open}
        onOpenChange={setOpen}
        interactions={[
          "hover",
          "click",
          "dismiss",
          "listNavigation",
          "typeahead",
        ]}
        role="menu"
        placement={rtl ? "left-start" : "right-start"}
        offset={0}
        hoverOptions={{
          delay: { open: 100, close: 0 },
        }}
        listNavigationOptions={{
          nested: true,
          parentOrientation: "vertical",
          rtl,
        }}
        floatingFocusManager={{
          initialFocus: -1,
          restoreFocus: false,
        }}
        ariaLabel={t("ui.menu.submenuAriaLabel", { text: option.text })}
        typeaheadListRef={submenuTypeaheadListRef}
      />
    );
  }

  return triggerElement;
};
